import React, { useState, useEffect } from "react";
import { Box, Typography, Chip, LinearProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { Project } from "../types";
import { getProjectStatusColor, projectStatusColors, ProjectStatusKey } from "../utils/statusColors";
import { fromServerToLocal } from "../utils/time";
import StatusChangeModal from "./Modals/StatusChangeModal";
import { getCachedStatuses } from "../storage/preloadData";

interface ProjectViewProps {
  project: Project;
  onStatusChange: (status: string) => Promise<void>;
  onStatusUpdateSuccess?: (message: string) => void;
}

const ProjectView: React.FC<ProjectViewProps> = ({ project, onStatusChange, onStatusUpdateSuccess }) => {
  const { t, i18n } = useTranslation();
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [availableStatuses, setAvailableStatuses] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const loadStatuses = async () => {
      try {
        const statuses = await getCachedStatuses();
        setAvailableStatuses(statuses.projects || []);
      } catch (err) {
        console.error("Failed to load project statuses:", err);
        setAvailableStatuses(["Draft", "Active", "Closed", "Paused", "Canceled"]);
      }
    };
    loadStatuses();
  }, []);

  const handleStatusSave = async (newStatus: string) => {
    setIsLoading(true);
    try {
      await onStatusChange(newStatus);
      if (onStatusUpdateSuccess) {
        onStatusUpdateSuccess(t("statusChangeModal.success") || "Статус успешно изменен");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const statusColorsMap: Record<string, string> = Object.keys(projectStatusColors).reduce(
    (acc, key) => {
      acc[key] = projectStatusColors[key as ProjectStatusKey].color;
      return acc;
    },
    {} as Record<string, string>
  );

  const dateFormat = i18n.language === "en_US" ? "DD.MM.YYYY" : "DD.MM.YYYY";

  const startLocal = project.dateStart
    ? fromServerToLocal(project.dateStart)
    : null;
  const endLocal = project.dateEnd ? fromServerToLocal(project.dateEnd) : null;

  const formattedStart =
    startLocal && dayjs(startLocal).isValid()
      ? dayjs(startLocal).format(dateFormat)
      : null;

  const formattedEnd =
    endLocal && dayjs(endLocal).isValid()
      ? dayjs(endLocal).format(dateFormat)
      : null;

  const normalizedStatus =
    project.status?.toLowerCase().replace(/\s/g, "_") || "";
  const statusText = t(`statuses.project.${normalizedStatus}`, project.status);
  const statusColor = getProjectStatusColor(project.status);

  const progress = project.cProgres !== null ? project.cProgres : 0;
  const getProgressColor = (value: number): string => {
    if (value <= 33) {
      return "#8B0000";
    } else if (value <= 66) {
      return "#FF8C00";
    } else {
      return "#228B22";
    }
  };
  const progressColor = getProgressColor(progress);

  return (
    <Box sx={{ width: "100%" }}>
      <Box display="flex" flexDirection="column" gap={2.5}>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              fontSize: "0.75rem",
              fontWeight: 500,
              mb: 0.5,
              display: "block",
            }}
          >
            {t("projects.detail.nameLabel") || "Название"}
          </Typography>
          <Typography
            variant="body1"
            sx={{ fontSize: "1rem", fontWeight: 500 }}
          >
            {project.name}
          </Typography>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexDirection: "row",
            gap: { xs: 3, sm: 4 },
            alignItems: "flex-start",
          }}
        >
          <Box
            sx={{
              flex: { xs: "1 1 auto", sm: 1 },
              minWidth: { xs: "140px", sm: "auto" },
              maxWidth: { xs: "45%", sm: "none" },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 0.5,
              }}
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: "0.75rem", fontWeight: 500 }}
              >
                {t("projects.detail.progressLabel") || "Прогресс"}
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.75rem",
                  color: progressColor,
                  fontWeight: 600,
                }}
              >
                {progress}%
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={progress}
              sx={{
                height: 8,
                borderRadius: 4,
                backgroundColor: "rgba(0, 0, 0, 0.08)",
                "& .MuiLinearProgress-bar": {
                  backgroundColor: progressColor,
                  borderRadius: 4,
                },
              }}
            />
          </Box>

          <Box sx={{ flex: { xs: "0 0 auto", sm: 1 }, ml: { xs: "0", sm: 0 } }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                mb: 0.5,
                display: "block",
              }}
            >
              {t("projects.detail.statusLabel") || "Статус"}
            </Typography>
            <Chip
              label={statusText}
              onClick={() => setStatusModalOpen(true)}
              sx={{
                backgroundColor: statusColor,
                color: "#fff",
                borderRadius: "16px",
                py: 0.5,
                fontSize: "0.875rem",
                fontWeight: 600,
                height: "auto",
                cursor: "pointer",
                "&:hover": {
                  opacity: 0.8,
                },
              }}
            />
          </Box>
        </Box>

        {(project.boardName || project.accountName) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "row",
              gap: { xs: 2, sm: 4 },
            }}
          >
            {project.boardName && (
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    mb: 0.5,
                    display: "block",
                  }}
                >
                  {t("projects.detail.boardLabel") || "Доска"}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                  {project.boardName}
                </Typography>
              </Box>
            )}
            {project.accountName && (
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    mb: 0.5,
                    display: "block",
                  }}
                >
                  {t("projects.detail.accountLabel") || "Контрагент"}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                  {project.accountName}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {(formattedStart || formattedEnd) && (
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", sm: "row" },
              gap: { xs: 2, sm: 4 },
            }}
          >
            {formattedStart && (
              <Box sx={{ flex: { xs: 1, sm: 1 } }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    mb: 0.5,
                    display: "block",
                  }}
                >
                  {t("projects.detail.startDateLabel") || "Дата начала"}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                  {formattedStart}
                </Typography>
              </Box>
            )}
            {formattedEnd && (
              <Box sx={{ flex: { xs: 1, sm: 1 } }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    mb: 0.5,
                    display: "block",
                  }}
                >
                  {t("projects.detail.endDateLabel") || "Дата окончания"}
                </Typography>
                <Typography variant="body1" sx={{ fontSize: "1rem" }}>
                  {formattedEnd}
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {project.description && (
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: "0.75rem",
                fontWeight: 500,
                mb: 0.5,
                display: "block",
              }}
            >
              {t("projects.detail.descriptionLabel") || "Описание"}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1rem",
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
              }}
            >
              {project.description}
            </Typography>
          </Box>
        )}
      </Box>
      <StatusChangeModal
        open={statusModalOpen}
        onClose={() => setStatusModalOpen(false)}
        onSave={handleStatusSave}
        currentStatus={project.status}
        availableStatuses={availableStatuses}
        statusType="project"
        statusColors={statusColorsMap}
        isLoading={isLoading}
      />
    </Box>
  );
};

export default ProjectView;
